import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  InputGroup,
  Badge,
  Dropdown,
  Spinner
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faPlus,
  faSearch,
  faSort,
  faDownload,
  faEye,
  faEdit,
  faEllipsisV,
  faSync,
  faHistory,
  faBell
} from '@fortawesome/free-solid-svg-icons'
import { Link, useParams, useHistory } from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import * as QuotationController from '../Controller/QuotationController'
import { getRequisitionsApi } from '../../Requisition/Controller/RequisitionController'
import '../Assets/css/quotation.scss'
import NotificationHistoryModal from '../Components/NotificationHistoryModal'
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal'
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary'
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert'
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction';


const QuotationList = (props) => {
  const { lng } = useParams()
  const history = useHistory()

  const [rows, setRows] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [sortField, setSortField] = useState('quotation_number')
  const [sortDirection, setSortDirection] = useState('desc')
  const [totalItems, setTotalItems] = useState(0)
  const [selectedRange, setSelectedRange] = useState('all')
  const [exporting, setExporting] = useState(false)
  const [showNotificationHistory, setShowNotificationHistory] = useState(false)
  const [referenceId, setReferenceId] = useState('')
  const [quotationStoreId, setQuotationStoreId] = useState('')
  const [notificationQuotationStatus, setNotificationQuotationStatus] = useState(null)
  const [notificationQuotationDueDate, setNotificationQuotationDueDate] = useState(null)
  const [showNotificationModal, setShowNotificationModal] = useState(false)
  const [pendingNotificationSlack, setPendingNotificationSlack] = useState('')


  const [filters, setFilters] = useState({
    limit: 10,
    offset: 0,
    sort: [
      {
        field: 'quotation_number',
        sort: 'desc'
      }
    ],
    search: '',
    from_created_date: '',
    to_created_date: ''
  })
  const suppressNextFetchRef = useRef(false)

  const setQuickRange = (type) => {
    const today = new Date()
    let from = ''
    let to = ''
    const fmt = (d) => d.toISOString().split('T')[0]
    switch (type) {
      case 'today':
        from = fmt(today)
        to = fmt(today)
        break
      case 'yesterday': {
        const y = new Date(today)
        y.setDate(y.getDate() - 1)
        from = fmt(y)
        to = fmt(y)
        break
      }
      case 'last7': {
        const f = new Date(today)
        f.setDate(f.getDate() - 6)
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'lastMonth': {
        const f = new Date(today)
        f.setDate(f.getDate() - 29)
        from = fmt(f)
        to = fmt(today)
        break
      }
      case 'all':
        from = ''
        to = ''
        break
      default:
        break
    }
    setFilters((prev) => ({
      ...prev,
      from_created_date: from,
      to_created_date: to,
      offset: 0
    }))
    setSelectedRange(type)
  }

  const handleLimitChange = (limit) => {
    const newLimit = parseInt(limit)
    setItemsPerPage(newLimit)
    setCurrentPage(1)
    setFilters((prev) => ({ ...prev, limit: newLimit, offset: 0 }))
  }

  const handleQuickRangeClick = (e) => {
    const range = e.currentTarget.getAttribute('data-range')
    if (range === 'custom') {
      setSelectedRange('custom')
      return
    }
    if (range === 'today') return setQuickRange('today')
    if (range === 'yesterday') return setQuickRange('yesterday')
    if (range === 'last7') return setQuickRange('last7')
    if (range === 'lastMonth') return setQuickRange('lastMonth')
    if (range === 'all') return setQuickRange('all')
  }

  const handleRefresh = () => {
    performFetch()
  }

  const performFetch = useCallback(
    async (effectiveFilters) => {
      try {
        props.loaderStateTrue()
        if (props.userEntryType?.user_type === 'staff') {
          if (localStorage.getItem('store_id') == null) {
            localStorage.setItem('store_id', props.userEntryType.branch_id)
          }
        }
        const response = await QuotationController.getQuotationsApi(
          effectiveFilters
        )
        if (response.success) {
          const r = response.data || []
          setRows(r)
          setTotalItems(response.total || r.length)
        } else {
          setRows([])
        }
      } catch (error) {
        console.error('Error fetching quotations:', error)
        setRows([])
      } finally {
        props.loaderStateFalse()
      }
    },
    [props.userEntryType]
  )

  const fetchRows = useCallback(async () => {
    await performFetch(filters)
  }, [filters, performFetch])

  useEffect(() => {
    if (suppressNextFetchRef.current) {
      suppressNextFetchRef.current = false
      return
    }
    fetchRows()
  }, [fetchRows])

  // Auto-fetch when custom dates are both selected
  useEffect(() => {
    if (
      selectedRange === 'custom' &&
      filters.from_created_date &&
      filters.to_created_date
    ) {
      performFetch({ ...filters })
    }
  }, [selectedRange, filters, performFetch])

  const getCreatedDisplay = (row) => {
    const value =
      row.createdAt || row.created_at || row.createdAt_label || row.created_on
    return value ? Utility.get12HourFormat(value) : 'N/A'
  }

  const getUpdatedDisplay = (row) => {
    const value =
      row.updatedAt || row.updated_at || row.updatedAt_label || row.updated_on
    return value ? Utility.get12HourFormat(value) : 'N/A'
  }

  const handleSearch = (e) => {
    const raw = e.target.value || ''
    const clean = raw.replace(/\s+/g, '')
    setSearchTerm(clean)
    setCurrentPage(1)
    const next = {
      ...filters,
      search: clean.length >= 2 ? clean : '',
      offset: 0
    }
    suppressNextFetchRef.current = true
    setFilters(next)
    if (clean.length >= 2 || clean.length === 0) {
      performFetch(next)
    }
  }

  // Removed Enter-key instant search to rely on debounced input

  const startIndex = totalItems === 0 ? 0 : filters.offset + 1
  const endIndex =
    totalItems === 0 ? 0 : Math.min(filters.offset + rows.length, totalItems)

  const handlePageChange = (page) => {
    setCurrentPage(page)
    setFilters((prev) => ({ ...prev, offset: (page - 1) * prev.limit }))
  }

  const handleSort = (field) => {
    let newSortDirection = 'asc'
    if (sortField === field) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
    } else {
      setSortField(field)
    }
    setSortDirection(newSortDirection)
    setFilters((prev) => ({
      ...prev,
      sort: [
        {
          field: field,
          sort: newSortDirection
        }
      ]
    }))
  }

  const getStatusBadge = (status) => {
    // Normalize status which may be number, string, or nested object
    let raw = status
    if (raw && typeof raw === 'object') {
      if (raw.status_data) {
        raw =
          raw.status_data.value_constant ||
          raw.status_data.value ||
          raw.status_data.label ||
          raw.status
      } else {
        raw = raw.value_constant || raw.value || raw.status || raw.label
      }
    }
    const key = typeof raw === 'string' ? raw.toUpperCase() : raw
    const map = {
      // Numeric
      0: { label: 'Cancelled', bg: 'danger' },
      1: { label: 'Pending', bg: 'primary' },
      2: { label: 'Accepted', bg: 'success' },
      3: { label: 'Declined', bg: 'danger' },
      4: { label: 'Expired', bg: 'danger' },
      // Constants
      CANCELLED: { label: 'Cancelled', bg: 'danger' },
      PENDING: { label: 'Pending', bg: 'primary' },
      ACCEPTED: { label: 'Accepted', bg: 'success' },
      DECLINED: { label: 'Declined', bg: 'danger' },
      EXPIRED: { label: 'Expired', bg: 'danger' }
    }
    const info = map[key] || { label: 'Unknown', bg: 'light' }
    return <Badge bg={info.bg}>{info.label}</Badge>
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const quotationPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'quotations',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'generate_invoice', 'change_status', 'accept', 'reject', 'send_notification', 'notification_history'],
    props.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  // Check if any actions are available for display in the table
  const hamburgerTableActions = quotationPermissions.view || quotationPermissions.edit || quotationPermissions.delete;

  const confirmationModalForSendNotification = (slack) => {
    setPendingNotificationSlack(slack)
    setShowNotificationModal(true)
  }

  const closeNotificationModal = () => {
    setShowNotificationModal(false)
    setPendingNotificationSlack('')
  }

  const confirmSendNotificationModal = async () => {
    await handleSendNotification(pendingNotificationSlack)
    closeNotificationModal()
  }

  const handleSendNotification = async (slack) => {
    try {
      props.loaderStateTrue();

      const resp = await QuotationController.sendQuotationNotificationApi(slack);

      if (resp?.success) {
        Utility.toastNotifications(resp?.message, 'Success', 'success');
      } else {
        Utility.toastNotifications(
          resp?.message || 'Failed to send notification',
          'Error',
          'error'
        );
      }
    } catch (error) {
      Utility.toastNotifications(
        'Failed to send notification',
        'Error',
        'error'
      );
    } finally {
      props.loaderStateFalse();
    }
  };

  const handleReferenceClick = async (referenceNumber) => {
    try {
      const response = await getRequisitionsApi({ search: referenceNumber, limit: 1, offset: 0 })
      if (response.success && response.data && response.data.length > 0) {
        const reqSlack = response.data[0].slack
        history.push(`/${lng}/sales/requisitions/view/${reqSlack}`)
      } else {
        Utility.toastNotifications('Requisition not found', 'Error', 'error')
      }
    } catch (error) {
      Utility.toastNotifications('Failed to find requisition', 'Error', 'error')
    }
  }

  const handleShowNotificationHistory = (referenceI_id, quotation_store_id, quotation_status, quotation_due_date) => {
    // console.log("quotation_store_id====",quotation_store_id)
    setShowNotificationHistory(true);
    setReferenceId(referenceI_id)
    setQuotationStoreId(quotation_store_id)
    setNotificationQuotationStatus(quotation_status)
    setNotificationQuotationDueDate(quotation_due_date)
  }

  return (
    <Container fluid className='p-0'>
      <div className='d-flex justify-content-between align-items-center top-header-box'>
        <h4>Quotations</h4>
        <div>
          {quotationPermissions.export && (
            <Button
              variant='outline-secondary'
              className='me-2'
              disabled={exporting}
              onClick={useCallback(async () => {
                try {
                  setExporting(true)
                  const exportData = {
                    from_created_date: filters.from_created_date,
                    to_created_date: filters.to_created_date
                  }
                  const response = await QuotationController.exportQuotationsApi(
                    exportData
                  )
                  if (response && response.success && response.data) {
                    if (response.isFileUrl) {
                      await Utility.downloadFile(
                        response.data,
                        response.filename || 'quotations_export.xlsx'
                      )
                    } else {
                      const blob = new Blob([response.data], {
                        type:
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                      })
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = response.filename || 'quotations_export.xlsx'
                      document.body.appendChild(a)
                      a.click()
                      window.URL.revokeObjectURL(url)
                      document.body.removeChild(a)
                    }
                  }
                } catch (e) {
                  console.error('Export quotations failed:', e)
                } finally {
                  setExporting(false)
                }
              }, [filters])}
            >
              <FontAwesomeIcon icon={faDownload} className='me-2' /> {exporting ? 'Downloading...' : 'Download'}
            </Button>
          )}
          {quotationPermissions.create && (
            <Button
              variant='primary'
              onClick={() => history.push(`/${lng}/sales/quotations/add`)}
            >
              <FontAwesomeIcon icon={faPlus} className='me-2' /> New Quotation
            </Button>
          )}
        </div>
      </div>

      <Card className='custom-border-light'>
        <Card.Body>
          {/* Removed From/To date search as requested */}

          <Row className='mb-3 gx-2 searchBarPanel'>
            <Col className='d-flex align-items-center flex-wrap gap-2 innSearchBarPanel'>
              {selectedRange === 'custom' && (
                <div className='d-flex align-items-center gap-2'>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    value={filters.from_created_date}
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        from_created_date: e.target.value,
                        offset: 0
                      }))
                      if (e.target.value && e.target.value > filters.to_created_date) {
                        setFilters((prev) => ({
                          ...prev,
                          to_created_date: '',
                          offset: 0
                        }))
                      }
                    }
                    }
                    style={{ width: 160 }}
                  />
                  <span>to</span>
                  <input
                    type='date'
                    className='form-control form-control-sm'
                    placeholder='dd.mm.yyyy'
                    min={filters.from_created_date || undefined}
                    value={filters.to_created_date}
                    onChange={(e) => {
                      setFilters((prev) => ({
                        ...prev,
                        to_created_date: e.target.value,
                        offset: 0
                      }))
                    }}
                    style={{ width: 160 }}
                  />
                </div>
              )}
              <div className='btn-group ms-auto'>
                <Button
                  size='sm'
                  variant={selectedRange === 'today' ? 'primary' : 'light'}
                  data-range='today'
                  onClick={handleQuickRangeClick}
                >
                  Today
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'yesterday' ? 'primary' : 'light'}
                  data-range='yesterday'
                  onClick={handleQuickRangeClick}
                >
                  Yesterday
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'last7' ? 'primary' : 'light'}
                  data-range='last7'
                  onClick={handleQuickRangeClick}
                >
                  Last 7 days
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'lastMonth' ? 'primary' : 'light'}
                  data-range='lastMonth'
                  onClick={handleQuickRangeClick}
                >
                  Last Month
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'custom' ? 'primary' : 'light'}
                  data-range='custom'
                  onClick={handleQuickRangeClick}
                >
                  Custom Dates
                </Button>
                <Button
                  size='sm'
                  variant={selectedRange === 'all' ? 'primary' : 'light'}
                  data-range='all'
                  onClick={handleQuickRangeClick}
                >
                  All
                </Button>
              </div>
              {/* <div className='d-flex align-items-center gap-2 ms-2'>
                <span>Show</span>
                <select
                  className='form-select form-select-sm'
                  style={{ width: 80 }}
                  value={itemsPerPage}
                  onChange={(e) => handleLimitChange(e.target.value)}
                >
                  {[10, 25, 50, 100, 500, 1000].map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                <span>entries</span>
              </div> */}
              <div className='searchFieldBoxes'>
                <div className='ms-2 searchFieldBox searchFieldBoxCustom'>
                  <InputGroup size='sm'>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <DebounceInput
                      minLength={1}
                      debounceTimeout={500}
                      className='form-control'
                      placeholder='Search by Quotation number, reference & bill to name'
                      value={searchTerm || ''}
                      onChange={(e) => handleSearch(e)}
                      onKeyDown={(e) => {
                        if (e.key === ' ') e.preventDefault()
                      }}
                    />
                  </InputGroup>
                </div>
                <div className='ms-2'>
                  <Button
                    variant='outline-secondary'
                    size='sm'
                    onClick={handleRefresh}
                    disabled={props.loaderState}
                    title='Refresh'
                  >
                    <FontAwesomeIcon
                      icon={faSync}
                      spin={props.loaderState}
                    />
                  </Button>
                </div>
              </div>

            </Col>
          </Row>

          <div className='table-responsive'>
            <Table hover className='align-middle'>
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('quotation_number')}
                    style={{ cursor: 'pointer' }}
                  >
                    Quotation Number{' '}
                    {sortField === 'quotation_number' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('quotation_reference')}
                    style={{ cursor: 'pointer' }}
                  >
                    Quotation Reference #{' '}
                    {sortField === 'quotation_reference' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                  <th
                    onClick={() => handleSort('store_name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Store Name{' '}
                    {sortField === 'store_name' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('bill_to')}
                    style={{ cursor: 'pointer' }}
                  >
                    Bill To{' '}
                    {sortField === 'bill_to' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('bill_to_name')}
                    style={{ cursor: 'pointer' }}
                  >
                    Bill To Name{' '}
                    {sortField === 'bill_to_name' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('quotation_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Quotation Date{' '}
                    {sortField === 'quotation_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('quotation_due_date')}
                    style={{ cursor: 'pointer' }}
                  >
                    Valid Until{' '}
                    {sortField === 'quotation_due_date' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th>
                    Items
                  </th>
                  <th
                    onClick={() => handleSort('total_order_amount')}
                    style={{ cursor: 'pointer' }}
                  >
                    Amount{' '}
                    {sortField === 'total_order_amount' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('status')}
                    style={{ cursor: 'pointer' }}
                  >
                    Status{' '}
                    {sortField === 'status' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  <th
                    onClick={() => handleSort('created_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Created On{' '}
                    {sortField === 'created_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {/* <th
                    onClick={() => handleSort('updated_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Updated On{' '}
                    {sortField === 'updated_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th> */}
                  <th
                    onClick={() => handleSort('created_at')}
                    style={{ cursor: 'pointer' }}
                  >
                    Created By{' '}
                    {sortField === 'created_at' && (
                      <FontAwesomeIcon icon={faSort} size='xs' />
                    )}
                  </th>
                  {hamburgerTableActions && (
                    <th>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {props.loaderState ? (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 13 : 12} className='text-center py-4'>
                      {/* <Spinner animation='border' variant='primary' />
                      <span className='ms-2'>Loading quotations...</span> */}
                    </td>
                  </tr>
                ) : rows.length > 0 ? (
                  rows.map((q) => (
                    <tr
                      key={q.id}
                      onDoubleClick={() => history.push(`/${lng}/sales/quotations/view/${q.slack}`)}
                      className='cursor-pointer'
                    >
                      <td>
                        {q.quotation_number}
                        {(() => {
                          try {
                            const parsed = JSON.parse(q.quotation_reference);
                            const refValue = Array.isArray(parsed) ? parsed.join(', ') : q.quotation_reference;
                            if (refValue && refValue !== '[]' && refValue !== '') {
                              return (
                                <div className="mt-1">
                                  <Badge
                                    bg="info"
                                    className="small cursor-pointer"
                                    style={{ textDecoration: 'none' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReferenceClick(refValue);
                                    }}
                                  >
                                    Ref: {refValue}
                                  </Badge>
                                </div>
                              );
                            }
                            return null;
                          } catch {
                            if (q.quotation_reference && q.quotation_reference !== '[]' && q.quotation_reference !== '') {
                              return (
                                <div className="mt-1">
                                  <Badge
                                    bg="info"
                                    className="small cursor-pointer"
                                    style={{ textDecoration: 'none' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReferenceClick(q.quotation_reference);
                                    }}
                                  >
                                    Ref: {q.quotation_reference}
                                  </Badge>
                                </div>
                              );
                            }
                            return null;
                          }
                        })()}
                      </td>
                      {/* <td>{q.quotation_reference || 'N/A'}</td> */}
                      <td>{q.store?.name || 'N/A'}</td>
                      <td>{q.bill_to}</td>
                      <td>{q.bill_to_name}</td>
                      <td>{q.quotation_date}</td>
                      <td>{q.quotation_due_date}</td>
                      <td>{q.products?.length || 0}</td>
                      <td>
                        {parseFloat(q.total_order_amount || 0).toFixed(2)}
                      </td>
                      <td>{getStatusBadge(q.status)}</td>
                      <td>{getCreatedDisplay(q)}</td>
                      {/* <td>{getUpdatedDisplay(q)}</td> */}
                      <td>{q.created_by?.first_name} {q.created_by?.last_name}</td>
                      {hamburgerTableActions && (
                        <td>
                          <Dropdown align='end'>
                            <Dropdown.Toggle
                              variant='light'
                              size='sm'
                              id={`dropdown-${q.id}`}
                              className='btn-icon no-caret'
                            >
                              <FontAwesomeIcon icon={faEllipsisV} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              {/* {quotationPermissions.view && ( */}
                              <Dropdown.Item
                                as={Link}
                                to={`/${lng}/sales/quotations/view/${q.slack}`}
                              >
                                <FontAwesomeIcon icon={faEye} className='me-2' />
                                View
                              </Dropdown.Item>
                              {/* )} */}
                              {quotationPermissions.edit && (q.status === 1) && (
                                <Dropdown.Item
                                  as={Link}
                                  to={`/${lng}/sales/quotations/edit/${q.slack}`}
                                >
                                  <FontAwesomeIcon
                                    icon={faEdit}
                                    className='me-2'
                                  />
                                  Edit
                                </Dropdown.Item>
                              )}
                              {quotationPermissions.send_notification && q.status !== 0 && q.status !== 3 && q.status !== 4 && !(q.status === 1 && q.quotation_due_date && new Date(q.quotation_due_date) < new Date(new Date().setHours(0, 0, 0, 0))) && (
                                <Dropdown.Item
                                  onClick={() => confirmationModalForSendNotification(q.slack)}
                                >
                                  <FontAwesomeIcon
                                    icon={faBell}
                                    className='me-2'
                                  />
                                  Send Notification
                                </Dropdown.Item>
                              )}
                              {quotationPermissions.notification_history && (
                                <Dropdown.Item
                                  // as={Link}
                                  onClick={() => handleShowNotificationHistory(q.id, q?.store_id, q.status, q.quotation_due_date)}
                                >
                                  <FontAwesomeIcon
                                    icon={faHistory}
                                    className='me-2'
                                  />
                                  Notification History
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={hamburgerTableActions ? 13 : 12} className='text-center py-4 nosearchResult'>
                      {searchTerm
                        ? 'No quotations found matching your search.'
                        : 'No quotations found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className='paginationSec'>
            <Pagination
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              siblingCount={2}
              onLimitChange={handleLimitChange}
              limitOptions={[10, 25, 50, 100, 500, 1000]}
              showSummary={true}
              showLimit={false}
            />
          </div>
        </Card.Body>
      </Card>

      <NotificationHistoryModal
        show={showNotificationHistory}
        onHide={() => setShowNotificationHistory(false)}
        referenceId={referenceId}
        storeId={quotationStoreId}
        quotationStatus={notificationQuotationStatus}
        quotationDueDate={notificationQuotationDueDate}
      />

      <ErrorBoundary title="ModalGlobal Error">
        <ModalGlobal
          show={showNotificationModal}
          onHide={closeNotificationModal}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Send Notification"
          footer={false}
          body={
            <>
              <ErrorBoundary title="ConfirmationAlert Error">
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to send the quotation notification?"
                  BodySecondContent=""
                  BodyThirdContent=""
                  confirmationButtonContent="Yes"
                  cancelButtonContent="No"
                  deleteConfirmButton={confirmSendNotificationModal}
                  deleteCancleButton={closeNotificationModal}
                />
              </ErrorBoundary>
            </>
          }
        />
      </ErrorBoundary>

    </Container>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType,
  loaderState: globalState.mainReducerData.loaderState
})

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse, handleActiveLink })(withNamespaces()(QuotationList))

